import { matchIndustryKey } from '../utils/commercialPlanCatalog';
import { ANALYTICS_INDUSTRIES, ANALYTICS_PLANS } from './events';

const PLAN_CODE_RE = /^(RE|IMM|EDU)_(STARTER|GROWTH|PROFESSIONAL|ENTERPRISE)$/i;

const PREFIX_TO_INDUSTRY = {
  RE: ANALYTICS_INDUSTRIES.REAL_ESTATE,
  IMM: ANALYTICS_INDUSTRIES.IMMIGRATION,
  EDU: ANALYTICS_INDUSTRIES.EDUCATION,
};

/**
 * @param {string|number|null|undefined} categoryIdOrName Industry category id, name, or label
 * @returns {AnalyticsIndustry|null}
 */
export function normalizeIndustry(categoryIdOrName) {
  if (categoryIdOrName == null || categoryIdOrName === '') return null;
  const asString = String(categoryIdOrName).trim();
  const numericMap = {
    1: ANALYTICS_INDUSTRIES.REAL_ESTATE,
    2: ANALYTICS_INDUSTRIES.IMMIGRATION,
    3: ANALYTICS_INDUSTRIES.EDUCATION,
  };
  if (numericMap[asString]) return numericMap[asString];
  const key = matchIndustryKey(asString);
  if (key === 'realestate' || key === 'immigration' || key === 'education') return key;
  return null;
}

/**
 * @param {string|null|undefined} tierOrLabel
 * @returns {string|null}
 */
export function normalizePlanTier(tierOrLabel) {
  if (!tierOrLabel) return null;
  const raw = String(tierOrLabel).trim().toLowerCase();
  const values = Object.values(ANALYTICS_PLANS);
  return values.includes(raw) ? raw : null;
}

/**
 * Parse commercial plan code (e.g. RE_GROWTH) into analytics params.
 * @param {string|null|undefined} planCode
 * @returns {{ industry: AnalyticsIndustry|null, plan: string|null }}
 */
export function parseCommercialPlanCode(planCode) {
  if (!planCode) return { industry: null, plan: null };
  const match = String(planCode).trim().toUpperCase().match(PLAN_CODE_RE);
  if (!match) return { industry: null, plan: null };
  return {
    industry: PREFIX_TO_INDUSTRY[match[1]] || null,
    plan: normalizePlanTier(match[2]),
  };
}

/**
 * @param {{ code?: string, Code?: string, subscriptionPlanName?: string, industryId?: number, industryName?: string }|null|undefined} plan
 * @param {string|number|null|undefined} [fallbackIndustry]
 * @returns {{ industry: AnalyticsIndustry|null, plan: string|null }}
 */
export function analyticsParamsFromPlan(plan, fallbackIndustry) {
  const fromCode = parseCommercialPlanCode(plan?.code || plan?.Code);
  if (fromCode.industry || fromCode.plan) {
    return {
      industry: fromCode.industry || normalizeIndustry(fallbackIndustry),
      plan: fromCode.plan,
    };
  }
  const name = (plan?.subscriptionPlanName || '').toLowerCase();
  let tier = null;
  if (name.includes('starter')) tier = ANALYTICS_PLANS.STARTER;
  else if (name.includes('growth')) tier = ANALYTICS_PLANS.GROWTH;
  else if (name.includes('professional')) tier = ANALYTICS_PLANS.PROFESSIONAL;
  else if (name.includes('enterprise')) tier = ANALYTICS_PLANS.ENTERPRISE;
  return {
    industry: normalizeIndustry(fallbackIndustry ?? plan?.industryId ?? plan?.industryName),
    plan: tier,
  };
}
